import {ActionList, type ActionListItemProps, Overlay, Portal} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {InputRange, InputStyleClone} from 'dom-input-range'
import type React from 'react'
import {useEffect, useId, useLayoutEffect, useRef, useState} from 'react'

import styles from './AutocompleteSuggestions.module.css'
import type {ShowSuggestionsEvent, Suggestion, Suggestions, SuggestionsPlacement} from './types'
import {getSelectionStart, getSuggestionKey, getSuggestionValue} from './utils'

/**
 * How the overlay is anchored to the trigger character. The two ways are mutually exclusive — the
 * union makes it a type error to pass both:
 * - `showEvent` for real `<input>`/`<textarea>` hosts (e.g. {@link InlineAutocomplete}); the overlay
 *   measures the trigger position internally with `InputRange`.
 * - `triggerCharCoords` for hosts whose target isn't a real input (e.g. CodeMirror) and therefore
 *   can't be measured internally; the host supplies externally-computed viewport coordinates.
 */
type AutoCompleteSuggestionsAnchorProps =
  | {
      /**
       * The show event that opened the picker. Carries the input element (`target`) and `query`, which
       * the overlay uses to measure where the trigger character sits so it can anchor itself there.
       * Required (though nullable) so callers can't omit both anchoring props and silently render at (0,0).
       */
      showEvent: ShowSuggestionsEvent | null
      triggerCharCoords?: never
    }
  | {
      /**
       * Externally-computed viewport coordinates of the trigger character, for hosts whose target is
       * not a real `<input>`/`<textarea>` and therefore can't be measured internally (e.g. CodeMirror).
       */
      triggerCharCoords: {top: number; left: number; height: number}
      showEvent?: never
    }

type AutoCompleteSuggestionsProps = {
  suggestions: Suggestions | null
  activeSuggestion?: Suggestion
  portalName?: string
  onClose: () => void
  onCommit: (suggestion: Suggestion) => void
  inputRef: React.RefObject<{focus(): void} | null>
  visible: boolean
  defaultPlacement: SuggestionsPlacement
  getSuggestionId: (suggestion: Suggestion) => string
  id: string
  menuTitle?: string
  menuTitleTrailingVisual?: React.ReactNode
  role: 'menu' | 'listbox'
  tabInsertsSuggestions: boolean
} & AutoCompleteSuggestionsAnchorProps

const LoadingIndicator = () => (
  <>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
    <ActionList.Item disabled>
      <SkeletonText />
    </ActionList.Item>
  </>
)

const SuggestionListItem = ({
  suggestion,
  onSelect,
  isActive,
  id,
  parentRole,
}: {
  suggestion: Suggestion
  onSelect: () => void
  isActive: boolean
  id: string
  parentRole: 'menu' | 'listbox'
}) => {
  const value = getSuggestionValue(suggestion)

  const sharedProps: React.LiHTMLAttributes<HTMLLIElement> & ActionListItemProps = {
    id,
    role: parentRole === 'menu' ? 'menuitem' : 'option',
    active: isActive,
    className: styles.suggestion,
    ['aria-selected']: parentRole === 'listbox' && isActive ? true : undefined,
    // onSelect/onClick won't work here; we want sto prevent default before focus leaves the input, so that focus remains
    // on the input. We don't need to handle key events here since that's done at the input level.
    onMouseDown: event => {
      event.preventDefault()
      onSelect()
    },
  }

  return typeof suggestion === 'string' ? (
    <ActionList.Item {...sharedProps}>{value}</ActionList.Item>
  ) : (
    suggestion.render({...sharedProps, children: value})
  )
}

/**
 * Renders an overlayed list at the given relative coordinates. Handles keyboard navigation
 * and accessibility concerns.
 */
export const AutocompleteSuggestions = ({
  suggestions,
  portalName,
  showEvent,
  triggerCharCoords,
  onClose,
  onCommit,
  inputRef,
  visible,
  defaultPlacement,
  activeSuggestion,
  getSuggestionId,
  id,
  menuTitle,
  menuTitleTrailingVisual,
  role,
  tabInsertsSuggestions,
}: AutoCompleteSuggestionsProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null)

  const [top, setTop] = useState(0)
  const [left, setLeft] = useState(0)
  const [height, setHeight] = useState<string>('auto')

  // Re-anchor the overlay when the input moves. `InputStyleClone.for(target)` is the shared mirror
  // `InputRange` uses internally; it observes scroll/resize/mutation and emits `update` on every input
  // event. We keep it alive only while the picker is open (disconnect on close) so it stops doing layout
  // work once dismissed. `update` fires synchronously per keystroke, so we read geometry in rAF to keep
  // `getBoundingClientRect` off the keystroke path; storing it as a string lets React's same-value
  // bail-out skip re-renders until the input actually moves.
  const [inputGeometry, setInputGeometry] = useState('')
  useEffect(() => {
    if (!visible) return
    const target = showEvent?.target
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return
    const clone = InputStyleClone.for(target)
    let frame = 0
    const onUpdate = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect()
        const geometry = `${rect.top},${rect.left},${rect.width},${rect.height},${target.scrollLeft},${target.scrollTop}`
        setInputGeometry(geometry)
      })
    }
    clone.addEventListener('update', onUpdate)
    return () => {
      cancelAnimationFrame(frame)
      clone.removeEventListener('update', onUpdate)
      clone.disconnect()
    }
  }, [visible, showEvent?.target])

  // Destructure the coordinate fields so a fresh-but-equal `{top, left, height}` object doesn't re-run
  // the positioning effect. `triggerCharTop === undefined` doubles as the "no external coords" check.
  const triggerCharTop = triggerCharCoords?.top
  const triggerCharLeft = triggerCharCoords?.left
  const triggerCharHeight = triggerCharCoords?.height

  useLayoutEffect(
    function recalculateTop() {
      // Measure the overlay's natural height. It grows to fit its content (`height: auto`) unless it
      // can't fit above or below, in which case it is clamped (the rare both-overflow branch below).
      const overlayHeight = overlayRef.current?.offsetHeight ?? 0

      // Measure where the trigger character sits. `getBoundingClientRect()` forces layout, so we do it
      // here in the layout effect (alongside the overlay-height read) instead of during render.
      let caretTop = 0
      let caretLeft = 0
      let caretHeight = 0
      if (visible && triggerCharTop !== undefined) {
        // Host supplied the coordinates directly because its target isn't a real `<input>`/`<textarea>`
        // (e.g. CodeMirror). Use them as-is and skip the internal forced-layout measurement.
        caretTop = triggerCharTop
        caretLeft = triggerCharLeft ?? 0
        caretHeight = triggerCharHeight ?? 0
      } else if (visible && showEvent) {
        const target = showEvent.target
        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
          const offset = (getSelectionStart(target) ?? 0) - showEvent.query.length
          const measured = new InputRange(target, offset).getBoundingClientRect()
          caretTop = measured.top
          caretLeft = measured.left
          caretHeight = measured.height
        }
      }
      const belowOffset = caretTop + caretHeight
      const wouldOverflowBelow = belowOffset + overlayHeight > window.innerHeight

      const aboveOffset = caretTop - overlayHeight
      const wouldOverflowAbove = aboveOffset < 0

      // Only override the default if it would overflow in the default direction and it would not overflow in the override direction
      let finalTopPosition: number = {
        below: wouldOverflowBelow && !wouldOverflowAbove ? aboveOffset : belowOffset,
        above: wouldOverflowAbove && !wouldOverflowBelow ? belowOffset : aboveOffset,
      }[defaultPlacement]

      // Sometimes the value can be NaN if layout is not available (ie, SSR or JSDOM)
      if (Number.isNaN(finalTopPosition)) finalTopPosition = 0

      // `auto` unless the overlay fits in neither direction, where we clamp to the available space.
      // (Reading `overlayHeight` against the committed height means the first recalc after a clamp needs
      // one extra commit to relax back to `auto` — pre-existing.)
      let nextHeight = 'auto'
      if (wouldOverflowBelow && wouldOverflowAbove) {
        nextHeight =
          defaultPlacement === 'above'
            ? `${window.innerHeight + finalTopPosition}px` // Space from top of overlay to bottom of viewport
            : `${window.innerHeight - finalTopPosition}px` // Space from overlay position to bottom of viewport
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLeft(caretLeft)
      // Ensure position is not negative (prevents overlay from appearing outside viewport)
      setTop(Math.max(0, finalTopPosition))
      setHeight(nextHeight)
    },
    // Re-runs on anything that affects position, including `inputGeometry` (updated when the input
    // moves). Setters bail on unchanged values, so steady typing triggers no re-render.
    [
      showEvent,
      triggerCharTop,
      triggerCharLeft,
      triggerCharHeight,
      suggestions,
      visible,
      defaultPlacement,
      inputGeometry,
    ],
  )

  const items = (
    <>
      {suggestions === 'loading' ? (
        <LoadingIndicator />
      ) : (
        suggestions?.map(suggestion => (
          <SuggestionListItem
            suggestion={suggestion}
            key={getSuggestionKey(suggestion)}
            id={getSuggestionId(suggestion)}
            onSelect={() => onCommit(suggestion)}
            isActive={activeSuggestion === suggestion}
            parentRole={role}
          />
        ))
      )}
    </>
  )
  const labelId = useId()
  // The Primer Overlay only calls `focus()` on this ref, so this cast is safe given the type enforcement for
  // `inputRef` (i.e. anything focusable will work)
  const typedInputRef = inputRef as unknown as React.RefObject<HTMLElement>

  /**
   * Even though we apply all the aria attributes, screen readers don't fully support this
   * dynamic use case and so they don't have a native way to indicate to the user when
   * there are suggestions available. So we use some hidden text with aria-live to politely
   * indicate what's available and how to use it.
   *
   * This text should be consistent and the important info should be first, because users
   * will hear it as they type - if they have heard the message before they should be able
   * to recognize it and quickly apply the first suggestion without listening to the rest
   * of the message.
   *
   * When screen reader users navigate using arrow keys, the `aria-activedescendant` will
   * change and will be read out so we don't need to handle that interaction here.
   */
  const announcedSuggestion =
    activeSuggestion ?? (Array.isArray(suggestions) && suggestions.length > 0 ? suggestions[0] : undefined)

  const description =
    !visible || !suggestions
      ? ''
      : suggestions === 'loading'
        ? 'Loading autocomplete suggestions…'
        : // It's important to include both Enter and Tab because we are telling the user that we are hijacking these keys:
          role === 'listbox'
          ? `${suggestions.length} autocomplete ${
              suggestions.length === 1 ? 'suggestion' : 'suggestions'
              /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
            } available; "${getSuggestionValue(announcedSuggestion!)}" is highlighted. Press ${
              tabInsertsSuggestions ? 'Enter or Tab' : 'Enter'
            } to select.`
          : ''

  // Conditional rendering appears wrong at first - it means that we are reconstructing the
  // Combobox instance every time the suggestions appear. But this is what we want - otherwise
  // the textarea would always have the `combobox` role, which is incorrect (a textarea should
  // not technically ever be a combobox). We compromise by dynamically applying the combobox
  // role only when suggestions are available.
  return visible ? (
    <>
      <Overlay
        onEscape={onClose}
        onClickOutside={onClose}
        returnFocusRef={typedInputRef}
        preventFocusOnOpen
        portalContainerName={portalName}
        top={top}
        left={left}
        ref={overlayRef}
        className={styles.Overlay_0}
      >
        <ActionList
          role={role}
          id={id}
          aria-label={menuTitle ? undefined : 'Autocomplete suggestions'}
          aria-labelledby={menuTitle ? labelId : undefined}
          className={styles.ActionList_0}
          style={{height}}
        >
          {menuTitle ? (
            <ActionList.Group>
              <ActionList.GroupHeading id={labelId}>
                {menuTitleTrailingVisual ? (
                  <span className={styles.MenuTitleWithVisual}>
                    {menuTitle}
                    {menuTitleTrailingVisual}
                  </span>
                ) : (
                  menuTitle
                )}
              </ActionList.GroupHeading>
              {items}
            </ActionList.Group>
          ) : (
            items
          )}
        </ActionList>
      </Overlay>

      <Portal>
        {/* This should NOT be linked to the input with aria-describedby or screen readers may not read the live updates.
        The assertive live attribute ensures the suggestions are read instead of the input label, which voiceover will try to re-read when the role changes. */}
        <span aria-live="assertive" aria-atomic style={{clipPath: 'circle(0)', position: 'absolute'}}>
          {description}
        </span>
      </Portal>
    </>
  ) : (
    <></>
  )
}
